import { useTenant } from '@emerald-works-nova/auth'
import useGroup from './useGroup'
import { FREE, BROWSER, SUSPENDED } from '../services/cognito/userGroups'
import { useMemo } from 'react'
import consts, { STATIC_AD_SLOTS } from '../consts'
import { useSession } from '@emerald-works/react-auth'

const useGoogleAdsense = () => {
  const tenant = useTenant()
  const { isInGroup } = useGroup()
  const { isAuthenticated } = useSession()

  const useAdsense = useMemo(() => {
    const isClub = tenant.pk === consts.DEFAULT_CLUB_PK
    // if we're not in the right tenant, we shouldn't see google adsense ads
    if (!isClub) {
      console.debug('*** Google Adsense hook - NOT CORRECT TENANT')
      return false
    }

    // PENG-3812: tenant-level kill switch, set from the Ark. Absent => enabled,
    // so a tenant that has never had the field written keeps showing ads.
    if (tenant?.features?.googleAds === false) {
      console.debug('*** Google Adsense hook - DISABLED FOR TENANT')
      return false
    }

    const isBrowser = isInGroup(BROWSER) || !isAuthenticated
    // if we're not a free, browser or suspended user, we shouldn't see google adsense ads
    if (!isInGroup(FREE) && !isBrowser && !isInGroup(SUSPENDED)) {
      console.debug('*** Google Adsense hook - NOT CORRECT USER GROUP')
      return false
    }

    // if we've got this far, we're good to show google adsense ads
    console.debug('*** Google Adsense hook - ENABLED, VALID TENANT ID, CORRECT USER GROUP - SHOWING AD(S)')
    return true
  }, [tenant.pk, tenant?.features?.googleAds, isInGroup, isAuthenticated])

  // Rules for when to allow static ads in content pages
  const shouldDisplayContentStaticAds = (content, adSlot) => {
    // Bespoke content will never have Ads because it's TinyMCE
    if (content?.primaryType?.includes('bespoke')) {
      return false
    }

    const { blocks, entityMap } = content.body.data

    // Filter empty unstyled blocks
    const filteredBlocks = blocks.filter(block => {
      if (block?.text?.trim()?.length === 0 && block.type === 'unstyled' && !block.inlineStyleRanges?.length && !block.entityRanges?.length) {
        return false
      }
      return true
    })

    // If there's no blocks with content, no Ads are displayed
    if (!filteredBlocks || !filteredBlocks.length) {
      return false
    }

    const contentBodyHasAds = entityMap && Object.keys(entityMap).some(key => {
      const entity = entityMap[key]
      return entity.type === 'ew-ads'
    })

    // Rules for the Header Static Ad Slot
    if (adSlot === STATIC_AD_SLOTS.HEADER) {
      // Infographics and Elearnings will always have the Header Ad
      if (content?.primaryType?.includes('infographic') || content?.primaryType?.includes('elearning')) {
        return content?.googleAds !== false
      }

      // Header Ad is only displayed if there are at least 4 blocks with content
      if (filteredBlocks.length >= 4) {
        return content?.googleAds !== false
      }
    }

    // Rules for the Right Side Bar Static Ad Slot
    if (adSlot === STATIC_AD_SLOTS.RIGHT_SIDE_BAR) {
      // If there's blocks and at least one of them is an Ad
      // We display the Right Side Bar Ad
      if (contentBodyHasAds) {
        return content?.googleAds !== false
      }
    }
  }

  return { useAdsense, shouldDisplayContentStaticAds }
}

export default useGoogleAdsense
